import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaCheck, FaPlus, FaExclamationTriangle } from 'react-icons/fa';

import {
   CRUD_ACTIONS,
   CRUD_ACTION_LABELS,
   actionLabel,
} from '../catalog';
import {
   columnPermissions,
   moduleStats,
   permissionMatches,
   resourceMatches,
} from '../permissionModel';
import { AMBER, BLUE, ChangedPill, GREEN, SensitivePill } from '../styles';

// ---------------------------------------------------------------------------
// Painel central — a matriz recurso × ação do módulo selecionado.
//
// A busca ATENUA (opacity) o que não casa em vez de esconder linhas — o
// operador mantém o mapa mental da matriz. Empty state só quando nada casa
// globalmente (prop `globalEmpty`, computada pela página).
//
// Em modo curadoria (contrato 0111), o clique numa célula/chip alterna o
// METADADO `is_sensitive` da permissão em vez da concessão ao papel.
// ---------------------------------------------------------------------------

const Panel = styled.div`
   display: flex;
   flex-direction: column;
   min-width: 0;
`;

const ModHead = styled.div`
   padding: 14px 18px 10px;
   border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ModTitleRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
`;

const ModName = styled.h3`
   margin: 0;
   font-size: 16px;
   font-weight: 800;
   color: ${({ theme }) => theme.colors.textPrimary};
`;

const ModCountPill = styled.span`
   display: inline-flex;
   align-items: center;
   height: 22px;
   padding: 0 9px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 800;
   background: ${({ $full }) => ($full ? GREEN.bgPill : BLUE.bgPill)};
   color: ${({ $full }) => ($full ? GREEN.text : BLUE.text)};
   font-variant-numeric: tabular-nums;
`;

const ModDescription = styled.p`
   margin: 4px 0 0;
   font-size: 12.5px;
   color: ${({ theme }) => theme.colors.textMuted};
   max-width: 680px;
`;

const HeadRow = styled.div`
   position: sticky;
   top: 0;
   z-index: 2;
   display: grid;
   grid-template-columns: minmax(0, 1fr) repeat(4, 74px);
   align-items: center;
   height: 42px;
   padding: 0 18px;
   background: #373737;
`;

const HeadLabel = styled.div`
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.06em;
   color: #fff;
   text-transform: uppercase;
`;

// Toggle de coluna inteira — controle local do domínio; declara o próprio
// :hover com background (G.4.1).
const ColumnToggle = styled.button`
   appearance: none;
   border: 0;
   background: transparent;
   font-family: inherit;
   cursor: pointer;
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 4px;
   height: 42px;
   padding: 0;
   border-radius: 6px;

   font-size: 10.5px;
   font-weight: 800;
   letter-spacing: 0.04em;
   color: #fff;
   text-transform: uppercase;

   &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.08);
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
   &:disabled {
      cursor: default;
   }
`;

const ColumnDash = styled.span`
   width: 22px;
   height: 4px;
   border-radius: 999px;
   background: ${({ $all, theme }) =>
      $all ? theme.colors.primarySoft : 'rgba(255, 255, 255, 0.22)'};
`;

const Body = styled.div`
   flex: 1;
   overflow-y: auto;
   overflow-x: hidden;
   max-height: calc(100vh - 300px);
`;

const Row = styled.div`
   display: grid;
   grid-template-columns: minmax(0, 1fr) repeat(4, 74px);
   align-items: center;
   padding: 12px 18px;
   border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
   opacity: ${({ $dimmed }) => ($dimmed ? 0.32 : 1)};

   &:hover {
      background: ${BLUE.bgHover};
   }
`;

const RowInfo = styled.div`
   min-width: 0;
   padding-right: 14px;
`;

// Fluxo INLINE (v2 do handoff): wrapper block + margin nos pills, para o
// título quebrar naturalmente em telas estreitas sem empurrar a grade.
const RowTitle = styled.div`
   display: block;

   ${SensitivePill}, ${ChangedPill} {
      margin-right: 6px;
      vertical-align: middle;
      white-space: nowrap;
   }
`;

const ResourceName = styled.span`
   display: inline;
   margin-right: 8px;
   font-size: 13.5px;
   font-weight: 700;
   color: #222;
   vertical-align: middle;
`;

const RowDescription = styled.p`
   margin: 3px 0 0;
   font-size: 12px;
   color: #6b6b6b;
   text-wrap: pretty;
`;

const ChipsRow = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 6px;
   margin-top: 8px;
`;

// Chip de ação extra — 3 estados (inativo / ativo comum / ativo sensível).
const ExtraChip = styled.button`
   appearance: none;
   font-family: inherit;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   gap: 5px;
   min-height: 24px;
   padding: 3px 9px;
   border-radius: 999px;
   font-size: 11.5px;
   font-weight: 700;
   white-space: nowrap;
   transition:
      background 120ms ease,
      border-color 120ms ease;

   border: 1px solid
      ${({ $state }) => {
         if ($state === 'common') return '#4098ef';
         if ($state === 'sensitive') return AMBER.border;
         return 'rgba(0, 0, 0, 0.16)';
      }};
   background: ${({ $state }) => {
      if ($state === 'common') return BLUE.bgPill;
      if ($state === 'sensitive') return AMBER.bgChip;
      return '#fff';
   }};
   color: ${({ $state }) => {
      if ($state === 'common') return BLUE.text;
      if ($state === 'sensitive') return AMBER.text;
      return '#666';
   }};
   opacity: ${({ $dimmed }) => ($dimmed ? 0.35 : 1)};

   svg {
      width: 12px;
      height: 12px;
   }

   &:hover:not(:disabled) {
      background: ${({ $state }) => {
         if ($state === 'common') return BLUE.bgActive;
         if ($state === 'sensitive') return AMBER.bgChip;
         return BLUE.bgHover;
      }};
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
   &:disabled {
      cursor: wait;
   }
`;

const CellSlot = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
`;

// Célula de ação 28×28 — os 4 estados do design + atenuação de busca.
const Cell = styled.button`
   appearance: none;
   font-family: inherit;
   cursor: pointer;
   width: 28px;
   height: 28px;
   padding: 0;
   border-radius: 8px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   transition:
      background 120ms ease,
      border-color 120ms ease;

   border: 1.5px solid
      ${({ $granted, $sensitive }) => {
         if ($granted && $sensitive) return AMBER.border;
         if ($granted) return '#4098ef';
         if ($sensitive) return AMBER.cellBorder;
         return 'rgba(0, 0, 0, 0.2)';
      }};
   background: ${({ $granted, $sensitive }) => {
      if ($granted && $sensitive) return AMBER.base;
      if ($granted) return '#4098ef';
      return '#fff';
   }};
   color: #fff;
   opacity: ${({ $dimmed }) => ($dimmed ? 0.35 : 1)};

   svg {
      width: 15px;
      height: 15px;
   }

   &:hover:not(:disabled) {
      background: ${({ $granted, $sensitive }) => {
         if ($granted && $sensitive) return AMBER.base;
         if ($granted) return '#4098ef';
         return BLUE.bgHover;
      }};
      ${({ $curation }) =>
         $curation
            ? `outline: 1.5px dashed ${AMBER.border}; outline-offset: 2px;`
            : ''}
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
   &:disabled {
      cursor: wait;
   }
`;

const Dash = styled.span`
   color: #cfcfcf;
   font-size: 13px;
   cursor: default;
`;

const Empty = styled.div`
   padding: 48px 18px;
   text-align: center;

   h4 {
      margin: 0;
      font-size: 14px;
      font-weight: 700;
      color: #444;
   }
   p {
      margin: 6px 0 0;
      font-size: 12.5px;
      color: #777;
   }
`;

function cellTitle(perm, curation) {
   if (curation) {
      return perm.isSensitive
         ? `Desmarcar como sensível · ${perm.key}`
         : `Marcar como sensível · ${perm.key}`;
   }
   const base = `${actionLabel(perm.action)} · ${perm.key}`;
   return perm.isSensitive ? `${base} · permissão sensível` : base;
}

export default function PermissionsMatrix({
   mod,
   granted,
   base,
   query,
   globalEmpty,
   onTogglePermission,
   onToggleColumn,
   curation,
}) {
   if (globalEmpty) {
      return (
         <Panel>
            <Empty>
               <h4>Nenhuma permissão encontrada</h4>
               <p>Ajuste a busca.</p>
            </Empty>
         </Panel>
      );
   }

   if (!mod) return <Panel />;

   const stats = moduleStats(mod, granted);
   const full = stats.total > 0 && stats.granted === stats.total;

   const handlePermissionClick = (resource, perm) => {
      if (curation.enabled) curation.onToggle(perm);
      else onTogglePermission(resource, perm);
   };

   return (
      <Panel>
         <ModHead>
            <ModTitleRow>
               <ModName>{mod.label}</ModName>
               <ModCountPill $full={full}>
                  {stats.granted} de {stats.total} concedidas
               </ModCountPill>
            </ModTitleRow>
            {mod.description ? (
               <ModDescription>{mod.description}</ModDescription>
            ) : null}
         </ModHead>

         <HeadRow>
            <HeadLabel>Recurso / Tela</HeadLabel>
            {CRUD_ACTIONS.map((action) => {
               const perms = columnPermissions(mod, action);
               const all =
                  perms.length > 0 && perms.every((perm) => granted.has(perm.id));
               return (
                  <ColumnToggle
                     key={action}
                     type="button"
                     disabled={curation.enabled || !perms.length}
                     title={
                        curation.enabled
                           ? 'Indisponível no modo curadoria'
                           : `${all ? 'Remover' : 'Conceder'} ${
                                CRUD_ACTION_LABELS[action]
                             } em todo o módulo ${mod.label}`
                     }
                     onClick={() => onToggleColumn(action)}
                  >
                     {CRUD_ACTION_LABELS[action]}
                     <ColumnDash $all={all} />
                  </ColumnToggle>
               );
            })}
         </HeadRow>

         <Body>
            {mod.resources.map((resource) => {
               const rowMatches = resourceMatches(resource, query);
               const rowSensitive = resource.permissions.some(
                  (perm) => perm.isSensitive
               );
               const rowChanged = resource.permissions.some(
                  (perm) => granted.has(perm.id) !== base.has(perm.id)
               );

               return (
                  <Row key={resource.key} $dimmed={Boolean(query) && !rowMatches}>
                     <RowInfo>
                        <RowTitle>
                           <ResourceName>{resource.label}</ResourceName>
                           {rowSensitive && (
                              <SensitivePill>
                                 <FaExclamationTriangle />
                                 Sensível
                              </SensitivePill>
                           )}
                           {rowChanged && <ChangedPill>alterado</ChangedPill>}
                        </RowTitle>
                        {resource.description ? (
                           <RowDescription>{resource.description}</RowDescription>
                        ) : null}
                        {resource.extras.length > 0 && (
                           <ChipsRow>
                              {resource.extras.map((perm) => {
                                 const isGranted = granted.has(perm.id);
                                 let state = 'inactive';
                                 if (isGranted) {
                                    state = perm.isSensitive
                                       ? 'sensitive'
                                       : 'common';
                                 }
                                 return (
                                    <ExtraChip
                                       key={perm.id}
                                       type="button"
                                       $state={state}
                                       $dimmed={
                                          Boolean(query) &&
                                          !permissionMatches(perm, resource, query)
                                       }
                                       disabled={curation.busyId === perm.id}
                                       title={cellTitle(perm, curation.enabled)}
                                       onClick={() =>
                                          handlePermissionClick(resource, perm)
                                       }
                                    >
                                       {isGranted ? <FaCheck /> : <FaPlus />}
                                       {actionLabel(perm.action)}
                                    </ExtraChip>
                                 );
                              })}
                           </ChipsRow>
                        )}
                     </RowInfo>

                     {CRUD_ACTIONS.map((action) => {
                        const perm = resource.cruds[action];
                        if (!perm) {
                           return (
                              <CellSlot key={action}>
                                 <Dash>—</Dash>
                              </CellSlot>
                           );
                        }
                        const isGranted = granted.has(perm.id);
                        return (
                           <CellSlot key={action}>
                              <Cell
                                 type="button"
                                 $granted={isGranted}
                                 $sensitive={Boolean(perm.isSensitive)}
                                 $curation={curation.enabled}
                                 $dimmed={
                                    Boolean(query) &&
                                    !permissionMatches(perm, resource, query)
                                 }
                                 disabled={curation.busyId === perm.id}
                                 title={cellTitle(perm, curation.enabled)}
                                 onClick={() =>
                                    handlePermissionClick(resource, perm)
                                 }
                              >
                                 {isGranted ? <FaCheck /> : null}
                              </Cell>
                           </CellSlot>
                        );
                     })}
                  </Row>
               );
            })}
         </Body>
      </Panel>
   );
}

PermissionsMatrix.propTypes = {
   mod: PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      description: PropTypes.string,
      resources: PropTypes.arrayOf(PropTypes.object),
   }),
   granted: PropTypes.instanceOf(Set).isRequired,
   base: PropTypes.instanceOf(Set).isRequired,
   query: PropTypes.string,
   globalEmpty: PropTypes.bool,
   onTogglePermission: PropTypes.func.isRequired,
   onToggleColumn: PropTypes.func.isRequired,
   curation: PropTypes.shape({
      enabled: PropTypes.bool,
      busyId: PropTypes.number,
      onToggle: PropTypes.func,
   }),
};

PermissionsMatrix.defaultProps = {
   mod: null,
   query: '',
   globalEmpty: false,
   curation: { enabled: false, busyId: null, onToggle: () => {} },
};
